import { useEffect, useState } from "react";

const RAIL_KEY = "wa_crm_rail";

/**
 * Whether the sidebar is a rail of icons or a column of labels.
 *
 * The collapsed state is a class on <html>, not React state. Three things move
 * with it - the rail, the brand block in the header and the content grid - and
 * only one of them is inside the rail component. A class on the root lets all
 * three follow one declaration instead of threading a boolean through the tree.
 *
 * Three inputs decide it, in this order:
 *
 *  - `enabled` is false at drawer widths. The icon rail is a desktop trade
 *    (labels for horizontal room), and inside a drawer that has room for labels
 *    it would only be a menu with its words removed. The stored preference is
 *    kept either way, so a phone visit does not reset the desktop choice.
 *  - `auto` is the workspace screens asking for the room. Mail and calls are
 *    three panes wide - folders, a list, the open item - and the nav labels are
 *    the least useful 60px on the screen while triaging. Walking into one of
 *    those screens collapses the rail without being asked.
 *  - the stored preference, for every other screen.
 *
 * The operator still outranks all of it: a click on the handle wins for as long
 * as they stay in that section, so the automatic collapse is a starting point
 * and not a lock. Moving to another section drops the override and the rail
 * goes back to following the two rules above - which is also what makes walking
 * back into mail collapse it again.
 */
export function useRailCollapse({
  enabled,
  auto,
  section,
}: {
  /** False while the sidebar is a drawer over the page. */
  enabled: boolean;
  /** True on the screens that want the rail out of the way by default. */
  auto: boolean;
  /** The section being viewed. A change to it clears the operator's override. */
  section: string;
}): [boolean, () => void] {
  const [pref, setPref] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(RAIL_KEY) === "mini";
  });
  /** null while the rail follows the rules; a boolean once the operator has
   *  said otherwise on the screen they are on. */
  const [override, setOverride] = useState<boolean | null>(null);

  const collapsed = enabled && (override ?? (pref || auto));

  // Keyed on the section rather than the path: opening another conversation is
  // still mail, and a rail the operator just opened must not shut again on
  // every click in the list.
  useEffect(() => {
    setOverride(null);
  }, [section]);

  useEffect(() => {
    document.documentElement.classList.toggle("crm-mini", collapsed);
  }, [collapsed]);

  useEffect(() => {
    window.localStorage.setItem(RAIL_KEY, pref ? "mini" : "wide");
  }, [pref]);

  // A click always sets the override, which is what the operator can see
  // happening. It only writes the stored preference on screens that did NOT
  // collapse by themselves: on mail and calls the rail was closed without being
  // asked, so opening it there means "not on this screen", not "never again".
  // Writing `pref` there would let one click on mail quietly un-collapse every
  // other screen the operator had deliberately set to icons.
  return [
    collapsed,
    () => {
      const next = !collapsed;
      if (!auto) setPref(next);
      setOverride(next);
    },
  ];
}
